<script setup lang="ts" generic="T">
import type { Table } from '@tanstack/vue-table';

import { ChevronLeftIcon, ChevronRightIcon, ChevronsLeft, ChevronsRight } from 'lucide-vue-next';

interface DataTablePaginationProps {
  table: Table<T>;
}
defineProps<DataTablePaginationProps>();
</script>

<template>
  <div class="flex items-center justify-between gap-[8px] bg-background">
    <!-- Left: Rows per page (hidden on mobile) + Page count -->
    <div class="flex items-center gap-3">
      <UiSelect
        :model-value="`${table.getState().pagination.pageSize}`"
        @update:model-value="(value: any) => table.setPageSize(Number(value))"
      >
        <UiSelectTrigger class="h-8 w-[85px]">
          <UiSelectValue :placeholder="`${table.getState().pagination.pageSize}`" />
        </UiSelectTrigger>
        <UiSelectContent side="top">
          <UiSelectItem
            v-for="pageSize in [20, 50, 100, 200]"
            :key="pageSize"
            :value="`${pageSize}`"
          >
            {{ pageSize }}
          </UiSelectItem>
        </UiSelectContent>
      </UiSelect>

      <div class="hidden sm:block text-sm font-medium whitespace-nowrap">
        {{ table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1 }}-{{
          Math.min(
            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
            table.getFilteredRowModel().rows.length,
          )
        }}
        of {{ table.getFilteredRowModel().rows.length }} calls
      </div>
    </div>

    <!-- Right: Navigation buttons -->
    <div class="flex items-center gap-1">
      <UiButton
        variant="outline"
        class="hidden lg:flex size-8 p-0"
        :disabled="!table.getCanPreviousPage()"
        @click="table.setPageIndex(0)"
      >
        <span class="sr-only">Go to first page</span>
        <ChevronsLeft class="size-4" />
      </UiButton>
      <UiButton
        variant="outline"
        class="size-8 p-0"
        :disabled="!table.getCanPreviousPage()"
        @click="table.previousPage()"
      >
        <span class="sr-only">Go to previous page</span>
        <ChevronLeftIcon class="size-4" />
      </UiButton>
      <UiButton
        variant="outline"
        class="size-8 p-0"
        :disabled="!table.getCanNextPage()"
        @click="table.nextPage()"
      >
        <span class="sr-only">Go to next page</span>
        <ChevronRightIcon class="size-4" />
      </UiButton>
      <UiButton
        variant="outline"
        class="hidden lg:flex size-8 p-0"
        :disabled="!table.getCanNextPage()"
        @click="table.setPageIndex(table.getPageCount() - 1)"
      >
        <span class="sr-only">Go to last page</span>
        <ChevronsRight class="size-4" />
      </UiButton>
    </div>
  </div>
</template>
